import { presetsTypes } from '@wix/fedops-presets'
// eslint-disable-next-line no-restricted-syntax
import { create, FedopsLogger } from '@wix/fedops-logger'
import type { AppIdentifier, InnerLoggerConfig } from '../types'
import { shouldReportEvent } from '../loggerHelper'

export const createFedopsLogger = ({
	biLoggerFactory,
	customParams = {},
	phasesConfig = 'SEND_ON_FINISH',
	appName = 'thunderbolt',
	presetType = process.env.PACKAGE_NAME === 'thunderbolt-ds' ? presetsTypes.DS : presetsTypes.BOLT,
	reportBlackbox = false,
	paramsOverrides = {},
	factory = create,
	muteThunderboltEvents = false,
	experiments = {},
}: InnerLoggerConfig): FedopsLogger => {
	const fedopsLogger = factory(appName, {
		presetType,
		phasesConfig,
		isPersistent: paramsOverrides.isPersistent ?? true,
		isServerSide: paramsOverrides.isServerSide ?? !process.env.browser,
		reportBlackbox,
		customParams,
		biLoggerFactory,
		// @ts-ignore FEDINF-3725
		paramsOverrides,
		enableSampleRateForAppNames:
			!!experiments['specs.thunderbolt.fedops_enableSampleRateForAppNames'] ??
			// @ts-ignore
			!!window?.viewerModel?.experiments['specs.thunderbolt.fedops_enableSampleRateForAppNames'],
	})
	const { interactionStarted, interactionEnded, appLoadingPhaseStart, appLoadingPhaseFinish } = fedopsLogger

	// This is done this way because FedopsLogger is a class and not an Object,
	// Therefor if we return an object it will crash because it operates on 'this' which does not exist in an object
	// so we can't make it immutable.

	fedopsLogger.interactionStarted = (interaction: string, ...args) => {
		if (shouldReportEvent(interaction, muteThunderboltEvents)) {
			return interactionStarted.call(fedopsLogger, interaction, ...args)
		} else {
			try {
				performance.mark(`${interaction} started`)
			} catch {}
		}
		return { timeoutId: 0 }
	}

	fedopsLogger.interactionEnded = (interaction: string, ...args) => {
		if (shouldReportEvent(interaction, muteThunderboltEvents)) {
			interactionEnded.call(fedopsLogger, interaction, ...args)
		} else {
			try {
				performance.mark(`${interaction} ended`)
			} catch {}
		}
	}
	fedopsLogger.appLoadingPhaseStart = (phase: string, appIdentifier?: AppIdentifier, ...args) => {
		if (shouldReportEvent(phase, muteThunderboltEvents, appIdentifier)) {
			appLoadingPhaseStart.call(fedopsLogger, phase, appIdentifier, ...args)
		} else {
			try {
				performance.mark(`${phase} started`)
			} catch {}
		}
	}
	fedopsLogger.appLoadingPhaseFinish = (phase: string, appIdentifier?: AppIdentifier, ...args) => {
		if (shouldReportEvent(phase, muteThunderboltEvents, appIdentifier)) {
			appLoadingPhaseFinish.call(fedopsLogger, phase, appIdentifier, ...args)
		} else {
			try {
				performance.mark(`${phase} finished`)
			} catch {}
		}
	}
	return fedopsLogger
}
